import { lazy, Suspense, type ComponentType, type ReactNode } from 'react';
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useParams,
} from 'react-router-dom';

import { Spinner } from '@kilat/ui';

import { PlatformBrandProvider, usePlatformBrand } from './lib/use-platform-brand.js';
import { ThemeProvider } from '@kilat/ui';

/**
 * This app opens dark and stays dark unless the viewer says otherwise.
 *
 * A named constant rather than an inline prop because the i18n lint rule reads
 * every JSX string attribute as user-visible copy — correctly, since that is
 * where hardcoded English usually hides. This one is a config value, and
 * naming it says so instead of suppressing the rule.
 */
const MARKETING_DEFAULT_THEME = 'dark' as const;
import { I18nProvider } from './lib/use-i18n.js';
import { prefixedLocales, stripLocalePrefix } from './lib/locale-routing.js';
const Home = lazyPage(() => import('./routes/index.js'), 'Home');
const HelpIndexPage = lazyPage(() => import('./routes/help.js'), 'HelpIndexPage');
const HelpArticlePage = lazyPage(() => import('./routes/help.js'), 'HelpArticlePage');
const FeaturesPage = lazyPage(() => import('./routes/features.js'), 'FeaturesPage');
const PricingPage = lazyPage(() => import('./routes/pricing.js'), 'PricingPage');
const AboutPage = lazyPage(() => import('./routes/about.js'), 'AboutPage');
const ContactPage = lazyPage(() => import('./routes/contact.js'), 'ContactPage');
const TosPage = lazyPage(() => import('./routes/legal.js'), 'TosPage');
const PrivacyPage = lazyPage(() => import('./routes/legal.js'), 'PrivacyPage');
const AgentAgreementPage = lazyPage(() => import('./routes/legal.js'), 'AgentAgreementPage');
const SignupPage = lazyPage(() => import('./routes/signup.js'), 'SignupPage');
const AgentQrPage = lazyPage(() => import('./routes/agent-qr.js'), 'AgentQrPage');
const BecomeAnAgentPage = lazyPage(
  () => import('./routes/become-an-agent.js'),
  'BecomeAnAgentPage',
);
const BecomeAnAgentSignupPage = lazyPage(
  () => import('./routes/become-an-agent-signup.js'),
  'BecomeAnAgentSignupPage',
);
const BecomeARecruiterPage = lazyPage(
  () => import('./routes/become-a-recruiter.js'),
  'BecomeARecruiterPage',
);
const BecomeARecruiterSignupPage = lazyPage(
  () => import('./routes/become-a-recruiter-signup.js'),
  'BecomeARecruiterSignupPage',
);
const RecruiterQrPage = lazyPage(() => import('./routes/recruiter-qr.js'), 'RecruiterQrPage');
const NotFoundPage = lazyPage(() => import('./routes/not-found.js'), 'NotFoundPage');

/**
 * Marketing site shell. Public, Kilat-branded, NOT a PWA.
 *
 * Locale-in-URL routing (SEO slice 2b — ADR-0024): the default locale (en)
 * lives at the bare path (`/`, `/pricing`, …) and is canonical; every other
 * supported locale is served from a single-segment prefix (`/ms`, `/zh/pricing`,
 * `/ta/help/getting-started`). The shared page set is mounted twice — once bare,
 * once under a `:locale` layout that validates the prefix — so each locale of
 * each page has a distinct, crawlable URL.
 *
 * Provider order (preserves the #70 brand-gate): `PlatformBrandProvider` stays
 * outermost and gates first paint on the `platformBrands` read (its retryable
 * error surface renders above i18n). `BrowserRouter` sits between the brand gate
 * and `I18nProvider` so the provider can derive the active locale from the URL.
 */

/** Pages shared by the bare (en) tree and the `:locale`-prefixed tree. */

/**
 * Load a route as its own chunk.
 *
 * Every marketing page used to be a STATIC import, so opening /pricing
 * downloaded all fourteen routes, every help article, and Firebase — a 1.76 MB
 * entry bundle. Measured on the live site 2026-08-22: first contentful paint
 * 8.2s and Lighthouse 56 on simulated mobile, against a >=90 budget. The pages
 * are pre-rendered, so that download was buying nothing at all for first paint;
 * it was purely the cost of hydrating.
 *
 * `React.lazy` wants a default export and these routes are named, so this
 * adapts them rather than churning fifteen files' export style.
 *
 * Safe with the prerenderer: it drives a REAL browser and waits for a heading
 * plus 150+ characters of text, so a chunk that resolves a tick later is still
 * captured in full (scripts/prerender.mjs).
 */
function lazyPage<K extends string>(load: () => Promise<Record<K, ComponentType>>, name: K) {
  return lazy(async () => {
    const mod = await load();
    // Widened through an explicit local: the indexed access
    // `Record<K, ComponentType>[K]` does not assign to `ComponentType` directly
    // under this compiler's settings, and a cast here would hide a real
    // mismatch if a route ever stopped being a component.
    const Component: ComponentType = mod[name];
    return { default: Component };
  });
}

const MARKETING_PAGES: readonly { readonly path: string; readonly element: ReactNode }[] = [
  { path: 'features', element: <FeaturesPage /> },
  { path: 'pricing', element: <PricingPage /> },
  { path: 'about', element: <AboutPage /> },
  { path: 'contact', element: <ContactPage /> },
  { path: 'help', element: <HelpIndexPage /> },
  { path: 'help/:slug', element: <HelpArticlePage /> },
  { path: 'legal/tos', element: <TosPage /> },
  { path: 'legal/privacy', element: <PrivacyPage /> },
  { path: 'legal/agent-agreement', element: <AgentAgreementPage /> },
  { path: 'signup', element: <SignupPage /> },
  { path: 'become-an-agent', element: <BecomeAnAgentPage /> },
  { path: 'become-an-agent/signup', element: <BecomeAnAgentSignupPage /> },
  { path: 'become-a-recruiter', element: <BecomeARecruiterPage /> },
  { path: 'become-a-recruiter/signup', element: <BecomeARecruiterSignupPage /> },
  // Per-code QR landings: robots-disallowed + never in the sitemap, but kept
  // locale-aware so a localised share link (`/ms/a/CODE`) resolves rather than
  // 404s.
  { path: 'a/:agentCode', element: <AgentQrPage /> },
  { path: 'r/:recruiterCode', element: <RecruiterQrPage /> },
];

function pageRoutes() {
  return MARKETING_PAGES.map((p) => <Route key={p.path} path={p.path} element={p.element} />);
}

/**
 * Layout for the `/:locale/*` subtree. Validates the prefix against the brand's
 * supported locales: an `/en/...` URL redirects to its canonical bare path, and
 * a non-locale first segment (`/nonsense`) renders the 404 rather than a blank
 * page (no-dead-ends). A valid prefix renders the matched child route.
 */
function LocaleLayout() {
  const brand = usePlatformBrand();
  const { locale } = useParams();
  const { pathname } = useLocation();
  const prefixed = prefixedLocales(brand.supportedLocales, brand.defaultLocale);

  if (locale === brand.defaultLocale) {
    const bare = stripLocalePrefix(pathname, [brand.defaultLocale, ...prefixed]);
    return <Navigate to={bare} replace />;
  }
  if (!locale || !(prefixed as readonly string[]).includes(locale)) {
    return <NotFoundPage />;
  }
  return <Outlet />;
}

export function App() {
  // Dark-first: this page's cinematic design is built around it and it is live
  // on kilatcarwash.net, so the default stays what it has always been. The
  // viewer can still choose light, and that choice wins.
  return (
    <ThemeProvider defaultChoice={MARKETING_DEFAULT_THEME}>
      {/*
        No fallback: the brand is seeded synchronously from this brand's own
        configuration, so there is no loading state to cover. It used to render
        a splash until a Firestore read returned, which threw away the
        pre-rendered page on every visit.
      */}
      <PlatformBrandProvider>
        <BrowserRouter>
          <I18nProvider>
            {/*
              Every route below is its own chunk, so one is always in flight on
              a cold navigation. The fallback is the SAME <Splash /> the brand
              provider uses, so a visitor sees one continuous branded screen
              rather than a flash of something else between the two waits.
            */}
            <Suspense fallback={<Splash />}>
              <Routes>
                <Route index element={<Home />} />
                {pageRoutes()}
                <Route path=":locale" element={<LocaleLayout />}>
                  <Route index element={<Home />} />
                  {pageRoutes()}
                  <Route path="*" element={<NotFoundPage />} />
                </Route>
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
          </I18nProvider>
        </BrowserRouter>
      </PlatformBrandProvider>
    </ThemeProvider>
  );
}

/** Pre-brand, pre-i18n splash — visual only, no copy is available yet. */
function Splash() {
  return (
    <main style={{ minHeight: '100dvh', display: 'grid', placeItems: 'center' }}>
      <Spinner size={24} color="#0d7a4f" />
    </main>
  );
}
